import { ICommonValue } from "@components/global/new/CommonSelector/CommonSelector";
import { useContracts } from "@utils/hooks/useContracts";
import { useIsFirstRender } from "@utils/hooks/useIsFirstRender";
import { useCallback, useEffect, useReducer, useState } from "react";
import { ContractPlanSelectorProps } from "./ContractPlanSelector";
import ContractPlanReducer, {
  ContractPlanSelectorActions,
  ContractPlanSelectorState,
  IPlanItem,
} from "./ContractPlanSelector.reducer";
import { useIntl } from "react-intl";
import { IContractValue } from "@components/global/new/ContractSelector/ContractSelector.props";
import { useMiningPlans } from "@utils/hooks/useMiningPlans";
import { IMiningPlanOptions } from "@services/getMinerList";

export interface UseContractPlanSelectorControlDeps {
  useContracts: () => IContractValue[];
  useIsFirstRender: () => boolean;
  useIntl: () => { formatMessage: (descriptor: { id: string }) => string };
  useMiningPlans: () => IMiningPlanOptions[];
  //formatMessage?: (id: string) => string;
}

export default function useContractPlanSelectorControl(
  props: ContractPlanSelectorProps,
  deps: UseContractPlanSelectorControlDeps = {
    useContracts,
    useIsFirstRender,
    useIntl,
    useMiningPlans,
  }
) {

  const { formatMessage } = deps.useIntl();
  const isFirstRender = deps.useIsFirstRender();
  const contractsRaw = deps.useContracts();
  const plans = deps.useMiningPlans();

  const NO_CONTRACT = {
    id: -1,
    text: formatMessage({ id: "contract_and_plan.no_contract" })
  } as ICommonValue<number>;

  const NO_PLAN = {
    id: 3,
    name: formatMessage({ id: "contract_and_plan.no_plan" })
  } as IPlanItem;

  const NO_PLAN_VALUE = {
    id: 3,
    text: formatMessage({ id: "contract_and_plan.no_plan" })
  } as ICommonValue<number>;

  const INITIAL_STATE: ContractPlanSelectorState = {
    plans: [],
    currentContractId: undefined,
    currentContractValue: undefined,
    currentPlanValue: undefined,
  };

  const [state, dispatch] = useReducer(ContractPlanReducer, INITIAL_STATE);
  const [prevPlan, setPrevPlan] = useState<number | undefined>();
  const [contracts, setContracts] = useState<ICommonValue<number>[]>([]);

  const parsedPlansToStoredPlans = useCallback((parsed: IPlanItem[]): IMiningPlanOptions[] => {
    return plans
      .filter(p => parsed.some((pp) => pp.id === p.id))
      .map(p => ({
        id: p.id,
        name: p.name,
        meta: "",
        schedule: p.schedule,
      }));
  }, [plans]);

  useEffect(() => {
    setPrevPlan(props.planId);
  }, [props.planId]);

  useEffect(() => {
    setContracts([
      NO_CONTRACT,
      ...contractsRaw.map((c) => ({
        id: c.id,
        text: c.name
      }))
    ]);
  }, [contractsRaw]);

  useEffect(() => {
    const current = contractsRaw.find((c) => c.id === props.contractId);
    if (current) {
      const payload: Partial<ContractPlanSelectorState> = {
        currentContractId: current.id,
        currentContractValue: {
          id: current.id,
          text: current.name,
        },
      };
      const parsed = JSON.parse(current.plans) as IPlanItem[];
      if (parsed && Array.isArray(parsed)) {
        payload.plans = parsedPlansToStoredPlans([
          NO_PLAN,
          ...parsed
        ]);
        const plan = parsed.find((p) => p.id === props.planId);
        if (plan) {
          payload.currentPlanValue = {
            id: plan.id,
            text: plan.name,
          };
        }
      }
      dispatch({
        type: ContractPlanSelectorActions.SET_VALUES,
        payload,
      });
    }
  }, [props.contractId, props.planId, contractsRaw]);

  useEffect(() => {
    if (isFirstRender) return;
    const payload: Partial<ContractPlanSelectorState> = {};
    if (state.currentContractId !== -1) {
      const current = contractsRaw.find((c) => c.id === state.currentContractId);
      if (current) {
        payload.currentContractValue = {
          id: current.id,
          text: current.name,
        };
        try {
          const parsed = JSON.parse(current.plans) as IPlanItem[];
          if (parsed && Array.isArray(parsed)) {
            payload.plans = parsedPlansToStoredPlans([
              NO_PLAN,
              ...parsed
            ]);
          } else {
            payload.plans = parsedPlansToStoredPlans([NO_PLAN]);
            payload.currentPlanValue = NO_PLAN_VALUE;
          }
        } catch {
          payload.plans = parsedPlansToStoredPlans([NO_PLAN]);
          payload.currentPlanValue = NO_PLAN_VALUE;
        }
      }
    } else {
      payload.currentPlanValue = NO_PLAN_VALUE;
      payload.currentContractValue = NO_CONTRACT;
    }
    dispatch({
      type: ContractPlanSelectorActions.SET_VALUES,
      payload,
    });
  }, [state.currentContractId]);

  const updateContractId = (value: any) => {
    dispatch({
      type: ContractPlanSelectorActions.SET_VALUES,
      payload: {
        currentContractId: value,
      },
    });
  };

  const clearContract = () => {
    dispatch({
      type: ContractPlanSelectorActions.SET_VALUES,
      payload: {
        currentContractId: undefined,
        currentPlanValue: undefined,
        currentContractValue: undefined,
        plans: [],
      },
    });
  };

  const clearPlan = () => {
    dispatch({
      type: ContractPlanSelectorActions.SET_VALUES,
      payload: {
        currentPlanValue: undefined,
      },
    });
  };

  const updatePlanValue = (value: number) => {
    dispatch({
      type: ContractPlanSelectorActions.UPDATE_PLAN_VALUE_BY_ID,
      payload: value,
    });
  };

  const [contract, setContract] = useState(
    state.currentContractValue?.id
  );
  const [plan, setPlan] = useState(
    state.currentPlanValue?.id
  );

  const onContractChanged = useCallback((value: number | undefined) => {
    props.onChange({
      contractId: value,
    });
    setContract(value);
    updateContractId(value);
  }, [plan, props.planId, props.onChange]);

  const onPlanChanged = useCallback((value: number) => {
    props.onChange({
      planId: value,
      prevPlanId: props.planId,
    });
    setPlan(value);
    updatePlanValue(value as any);
  }, [contract, props.planId, props.onChange]);

  return {
    contracts,
    state,
    prevPlan,
    updateContractId,
    clearContract,
    clearPlan,
    updatePlanValue,
    onContractChanged,
    onPlanChanged,
  };
}
